import React, { useState } from "react";
import { ArrowRight, Check, HelpCircle, ExternalLink } from "lucide-react";
import "@/components/sections/VideoIntro.css";
import { analytics, track } from "@/lib/analytics";
import { eventConfig } from "@/config/eventConfig";
import { loadSelfCheck, saveSelfCheck } from "@/lib/selfCheck";
import { QuestionCloud } from "@/components/sections/QuestionCloud";

// "Sound familiar?" — the 90-second promo (Nicholas & Alicia) in a phone frame,
// with Alicia's four questions as a self-check and the longevity statistic.
const VideoIntro = ({ onRegister }) => {
  const { video } = eventConfig;
  const [answers, setAnswers] = useState(() => loadSelfCheck()); // index -> "yes" | "no"

  const answered = Object.keys(answers).length;
  const unsure = Object.values(answers).filter((v) => v === "no").length;
  const complete = answered === video.questions.length;

  const answer = (i, value) => {
    const next = { ...answers, [i]: value };
    setAnswers(next);
    saveSelfCheck(next); // carried into the registration form's "Biggest CPF Question"
    track("video_selfcheck_answer", { index: i, value });
  };

  const handleReserve = () => {
    analytics.primaryCtaClick({ source: "watch-section", unsure });
    if (typeof onRegister === "function") onRegister();
  };

  const verdict = () => {
    if (!complete) return null;
    if (unsure === 0) return "You know your CPF better than most. Come sharpen the details and hear the latest 2026 updates first-hand.";
    if (unsure <= 2) return `You were unsure about ${unsure} of the 4. The seminar covers every one of them in plain English \u2014 and we\u2019ll add them to your registration so the team knows what to cover for you.`;
    return "Alicia was unsure about all of them too. Two hours on 1 October will change that \u2014 we\u2019ll add these questions to your registration so the team knows what to cover for you.";
  };

  return (
    <section className="vid-section" id="watch" aria-labelledby="vid-title" data-testid="video-intro">
      <QuestionCloud answers={answers} />
      <div className="vid-container">
        {/* Phone frame */}
        <div className="vid-phone-wrap">
          <div className="vid-phone" data-testid="video-phone-frame">
            <span className="vid-phone__notch" aria-hidden="true" />
            <div className="vid-phone__screen">
              <iframe
                data-testid="video-iframe"
                src={video.embedUrl}
                title={video.title}
                frameBorder="0"
                loading="lazy"
                allow="fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                allowFullScreen
              />
            </div>
          </div>
          <p className="vid-phone__caption" data-testid="video-caption">
            <strong>{video.title}</strong> &middot; {video.durationLabel} &middot; Nicholas &amp; Alicia
          </p>
          <a className="vid-share" href={video.shareUrl} target="_blank" rel="noopener noreferrer" data-testid="video-open-vimeo">
            Open on Vimeo <ExternalLink size={12} />
          </a>
        </div>

        {/* Copy + self-check */}
        <div className="vid-copy">
          <p className="vid-eyebrow">{video.eyebrow}</p>
          <h2 id="vid-title">{video.heading}</h2>
          <p className="vid-intro">{video.supporting}</p>

          <div className="vid-check" data-testid="video-selfcheck">
            <div className="vid-check__head">
              <strong>Can you answer these?</strong>
              <span data-testid="video-selfcheck-progress">{answered}/{video.questions.length}</span>
            </div>
            <ol className="vid-check__list">
              {video.questions.map((item, i) => {
                const v = answers[i];
                return (
                  <li key={item.q} className={`vid-q${v ? ` is-${v}` : ""}`} data-testid={`video-question-${i}`}>
                    <span className="vid-q__num" aria-hidden="true">{i + 1}</span>
                    <div className="vid-q__body">
                      <p className="vid-q__text">{item.q}</p>
                      {v && <p className="vid-q__hint" data-testid={`video-question-hint-${i}`}>{item.hint}</p>}
                    </div>
                    <div className="vid-q__btns" role="group" aria-label={`Can you answer question ${i + 1}?`}>
                      <button type="button" aria-pressed={v === "yes"} onClick={() => answer(i, "yes")} data-testid={`video-question-${i}-yes`}>
                        <Check size={13} /> Yes
                      </button>
                      <button type="button" aria-pressed={v === "no"} onClick={() => answer(i, "no")} data-testid={`video-question-${i}-no`}>
                        <HelpCircle size={13} /> Not sure
                      </button>
                    </div>
                  </li>
                );
              })}
            </ol>
            {complete && (
              <p className="vid-check__verdict" role="status" data-testid="video-selfcheck-verdict">{verdict()}</p>
            )}
          </div>

          <figure className="vid-stat" data-testid="video-stat">
            <div className="vid-stat__num">~{video.stat.value}</div>
            <figcaption>
              <p>{video.stat.label}</p>
              <p className="vid-stat__caption">{video.stat.caption}</p>
              <a href={video.stat.sourceUrl} target="_blank" rel="noopener noreferrer" data-testid="video-stat-source">
                Source: {video.stat.sourceLabel} <ExternalLink size={11} />
              </a>
            </figcaption>
          </figure>

          <div className="vid-covers">
            <strong>At the seminar you will learn</strong>
            <ul>
              {video.covers.map((c) => (
                <li key={c}><Check size={14} /> {c}</li>
              ))}
            </ul>
          </div>

          <button type="button" className="vid-cta" onClick={handleReserve} data-testid="video-reserve-button">
            {video.ctaLabel} <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default VideoIntro;
